import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Target, AlertTriangle, ChevronRight } from "lucide-react";
import { apiClient } from "@/lib/apiClient";

const chf = (v) => `CHF ${Number(v || 0).toLocaleString("de-CH", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/** Cockpit: Firmen-Budget Soll/Ist (Konten + Kostenstellen) mit Warnung bei Überschreitung. */
export default function BudgetCockpitCard({ companyId }) {
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  useEffect(() => {
    if (!companyId) return;
    apiClient.get(`/companies/${companyId}/budgets/cockpit`).then((r) => setD(r.data)).catch(() => setD(null));
  }, [companyId]);
  if (!d) return null;
  if (!d.has_budget) {
    return (
      <Card className="p-4 border border-dashed border-slate-200 bg-white flex items-center justify-between cursor-pointer hover:border-slate-400" onClick={() => navigate("/reports?tab=budget")} data-testid="budget-cockpit-empty">
        <div className="flex items-center gap-2 text-sm text-slate-600"><Target className="h-4 w-4 text-slate-500" />Noch kein Jahresbudget {d.year} – Budget pro Konto oder Kostenstelle festlegen, um Soll/Ist hier zu sehen.</div>
        <ChevronRight className="h-4 w-4 text-slate-400" />
      </Card>
    );
  }
  const t = d.totals_expense_ytd;
  const pct = t.pct ?? 0;
  const over = t.variance > 0;
  const bar = over ? "bg-rose-500" : pct >= 90 ? "bg-amber-500" : "bg-emerald-500";
  return (
    <Card className="p-5 border border-slate-200 bg-white" data-testid="budget-cockpit-card">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
        <div className="flex items-start gap-2">
          <Target className="h-4 w-4 text-slate-700 mt-0.5" />
          <div>
            <div className="text-sm font-medium text-slate-900">Budget {d.year} · Soll/Ist bis Monat {d.current_month}</div>
            <div className="text-[11px] text-slate-500">{d.lines} Budgetzeilen (Konten &amp; Kostenstellen) · Aufwand kumuliert</div>
          </div>
        </div>
        <button className="text-xs text-slate-600 underline" onClick={() => navigate("/reports?tab=budget")} data-testid="budget-cockpit-link">Budget bearbeiten</button>
      </div>
      <div className="grid md:grid-cols-3 gap-3 mb-3">
        <div className="rounded-md border border-slate-200 p-3"><div className="text-[11px] text-slate-500">Budget bis heute</div><div className="text-lg font-semibold text-slate-900" data-testid="budget-cockpit-soll">{chf(t.budget)}</div></div>
        <div className="rounded-md border border-slate-200 p-3"><div className="text-[11px] text-slate-500">Ist bis heute</div><div className={`text-lg font-semibold ${over ? "text-rose-700" : "text-slate-900"}`} data-testid="budget-cockpit-ist">{chf(t.actual)}</div></div>
        <div className="rounded-md border border-slate-200 p-3"><div className="text-[11px] text-slate-500">Abweichung</div><div className={`text-lg font-semibold ${over ? "text-rose-700" : "text-emerald-700"}`} data-testid="budget-cockpit-abweichung">{t.variance > 0 ? "+" : ""}{chf(t.variance)}</div><div className="text-[11px] text-slate-500">{pct} % des Budgets verbraucht</div></div>
      </div>
      <div className="h-2 w-full rounded-full bg-slate-100 overflow-hidden mb-3"><div className={`h-full ${bar}`} style={{ width: `${Math.min(100, pct)}%` }} /></div>
      {d.alerts.length > 0 ? (
        <div className="space-y-1" data-testid="budget-cockpit-alerts">
          <div className="flex items-center gap-1.5 text-xs font-medium text-amber-800"><AlertTriangle className="h-3.5 w-3.5" />{d.over_count} über Budget · {d.warn_count} nahe am Budget</div>
          {d.alerts.map((a) => (
            <div key={a.id} className="flex items-center justify-between text-xs border-b border-slate-50 py-1" data-testid={`budget-alert-${a.id}`}>
              <span className="text-slate-700"><span className={`inline-block w-2 h-2 rounded-full mr-2 ${a.status === "over" ? "bg-rose-500" : "bg-amber-400"}`} />{a.label}{a.target_type === "cost_center" && <span className="ml-1 text-[10px] text-slate-400">Kostenstelle</span>}</span>
              <span className={`tabular-nums ${a.status === "over" ? "text-rose-700 font-medium" : "text-amber-700"}`}>{chf(a.ytd.actual)} / {chf(a.ytd.budget)} ({a.ytd.variance > 0 ? "+" : ""}{a.ytd.variance_pct ?? 0} %)</span>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-xs text-emerald-700" data-testid="budget-cockpit-ok">Alle Budgetzeilen im Rahmen.</div>
      )}
    </Card>
  );
}
